import { Component, OnInit, ViewChild } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { BillingSettingsService } from "../billing/settings/billing-settings.service";
import { ToastrService } from "../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import {
  ChangeBillingSettingsAction,
  GetBillingSettingsAction,
} from "../settings/settings.actions";
import { getBillingSettingsSelector } from "../settings/settings.reducer";
import { BillingSettings } from "../settings/settings.interface";
import { MatExpansionPanel } from "@angular/material/expansion";
import { AuthService } from "../auth/auth.service";

@Component({
  selector: "app-expired-trial",
  templateUrl: "./expired-trial.component.html",
  styleUrls: ["./expired-trial.component.scss"],
  standalone: false,
})
export class ExpiredTrialComponent implements OnInit {
  @ViewChild("contactPanel") contactPanel: MatExpansionPanel;
  @ViewChild("planPanel") planPanel: MatExpansionPanel;

  isProcessingPayment = false;
  isUserLoaded = false;
  hasBillingRole = false;

  form: FormGroup = new FormGroup({
    planName: new FormControl(""),
    price: new FormControl({ value: "", disabled: true }, [
      Validators.pattern(/^\d+(\.\d{1,2})?$/),
    ]),
    currency: new FormControl({ value: "", disabled: true }),
    planId: new FormControl({ value: "", disabled: true }),
    subscriptionStartDate: new FormControl({ value: "", disabled: true }),
    lastInvoiceUserCount: new FormControl({ value: "", disabled: true }),
    currentUserCount: new FormControl({ value: "", disabled: true }),
    subscriptionPeriod: new FormControl(""),
    userCountRange: new FormControl(""),
    contactName: new FormControl(""),
    contactPhone: new FormControl(""),
    contactEmail: new FormControl(""),
    companyName: new FormControl(""),
    country: new FormControl(""),
    street: new FormControl(""),
    streetNumber: new FormControl(""),
    city: new FormControl(""),
    postalCode: new FormControl(""),
    state: new FormControl(""),
    vatNumber: new FormControl(""),
    vatRate: new FormControl(""),
  });

  plans = ["freemium", "basic", "premium"];
  currencies = ["USD", "EUR", "PLN"];

  constructor(
    private billingSettingsService: BillingSettingsService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store,
    public auth: AuthService,
  ) {}

  ngOnInit(): void {
    void this.auth.fetchCurrent().then(() => {
      const appUser = this.auth.appUser;
      this.hasBillingRole =
        appUser &&
        [
          "SUPER_ADMIN",
          "RESELLER",
          "GROUP_MANAGER",
          "COMPANY_MANAGER",
        ].includes(appUser.role);
      this.isUserLoaded = true;

      if (this.hasBillingRole) {
        this.store.dispatch(new GetBillingSettingsAction());
        this.store
          .select(getBillingSettingsSelector)
          .subscribe((billingSettings: BillingSettings) => {
            if (billingSettings) {
              this.fillForm(billingSettings);
            }
          });
      }
    });

    this.form.get("subscriptionPeriod")?.valueChanges.subscribe(() => {
      this.updatePrice();
    });

    this.form.get("planName")?.valueChanges.subscribe(() => {
      this.updatePrice();
    });

    this.form.get("country")?.valueChanges.subscribe((alpha2: string) => {
      this.form.get("vatRate")?.setValue(alpha2 === "PL" ? 23 : 0);
    });
  }

  fillForm(billingSettings: BillingSettings): void {
    this.form.patchValue(
      {
        planName: billingSettings.planName,
        price: billingSettings.price,
        currency: billingSettings.currency,
        planId: billingSettings.planId,
        subscriptionStartDate: new Date(billingSettings.subscriptionStartDate),
        lastInvoiceUserCount: billingSettings.lastInvoiceUserCount,
        currentUserCount: billingSettings.currentUserCount,
        subscriptionPeriod: billingSettings.subscriptionPeriod,
        userCountRange: billingSettings.userCountRange,
        contactName: billingSettings.contactName || "",
        contactPhone: billingSettings.contactPhone || "",
        contactEmail: billingSettings.contactEmail || "",
        companyName: billingSettings.companyName || "",
        country: billingSettings.country || "",
        street: billingSettings.street || "",
        streetNumber: billingSettings.streetNumber || "",
        city: billingSettings.city || "",
        postalCode: billingSettings.postalCode || "",
        state: billingSettings.state || "",
        vatNumber: billingSettings.vatNumber || "",
        vatRate: billingSettings.vatRate,
      },
      { emitEvent: false },
    );
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue() as BillingSettings;
      const payload = this.nullishBillingPayload(formValue);
      if ((payload.subscriptionStartDate as unknown) instanceof Date) {
        (payload as unknown as Record<string, string>).subscriptionStartDate =
          payload.subscriptionStartDate.toISOString();
      }
      this.store.dispatch(new ChangeBillingSettingsAction(payload));
    } else {
      this.form.markAllAsTouched();
    }
  }

  openPaymentModal(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue() as BillingSettings;
    const payload = this.nullishBillingPayload(formValue);
    if ((payload.subscriptionStartDate as unknown) instanceof Date) {
      (payload as unknown as Record<string, string>).subscriptionStartDate =
        payload.subscriptionStartDate.toISOString();
    }

    this.isProcessingPayment = true;

    this.billingSettingsService.update(payload).subscribe({
      next: () => {
        this.billingSettingsService.validateBillingInfo().subscribe({
          next: (response: { success: boolean; missingFields?: string[] }) => {
            if (response.success) {
              this.billingSettingsService
                .createCheckoutSession("expired")
                .subscribe({
                  next: (stripeResponse: { url: string }) => {
                    window.location.href = stripeResponse.url;
                  },
                  error: () => {
                    this.toastr.error(
                      this.translate.instant(
                        "trial.expired.paymentError",
                      ) as string,
                    );
                    this.isProcessingPayment = false;
                  },
                });
            } else {
              this.toastr.warning(
                this.translate.instant(
                  "trial.expired.fillRequiredFields",
                ) as string,
              );
              this.highlightMissingFields(response.missingFields || []);
              if (this.contactPanel && !this.contactPanel.expanded) {
                this.contactPanel.open();
              }
              setTimeout(() => {
                const formSection = document.getElementById(
                  "billing-info-section",
                );
                if (formSection) {
                  formSection.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }, 300);
              this.isProcessingPayment = false;
            }
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("trial.expired.validationError") as string,
            );
            this.isProcessingPayment = false;
          },
        });
      },
      error: () => {
        this.toastr.error(
          (this.translate.instant("trial.expired.updateError") as string) ||
            "Błąd podczas zapisywania ustawień",
        );
        this.isProcessingPayment = false;
      },
    });
  }

  private highlightMissingFields(missingFields: string[]): void {
    missingFields.forEach((field) => {
      const formControl = this.form.get(field);
      if (formControl) {
        formControl.markAsTouched();
        formControl.setErrors({ required: true });
        const matFormField = document.querySelector(
          `mat-form-field[formControlName="${field}"]`,
        );
        if (matFormField) {
          matFormField.classList.add("mat-form-field-error");
        }
      }
    });
  }

  updatePrice(): void {
    const plan = this.form.get("planName")?.value as string;
    const period = this.form.get("subscriptionPeriod")?.value as string;
    if (plan && period) {
      const priceMapping: { [key: string]: { [key: string]: number } } = {
        freemium: { monthly: 0, yearly: 0 },
        basic: { monthly: 0.99, yearly: 0.89 },
        premium: { monthly: 1.99, yearly: 1.79 },
      };
      const planLower: string = plan.toLowerCase();
      const periodLower: string = period.toLowerCase();
      const newPrice = priceMapping[planLower]
        ? priceMapping[planLower][periodLower]
        : null;
      if (newPrice !== null) {
        this.form.get("price")?.setValue(newPrice);
      }
    }
  }

  private nullishBillingPayload(formValue: BillingSettings): BillingSettings {
    const payload = { ...formValue } as BillingSettings &
      Record<string, unknown>;
    const textFields = [
      "contactName",
      "contactPhone",
      "contactEmail",
      "companyName",
      "country",
      "street",
      "streetNumber",
      "city",
      "postalCode",
      "state",
      "vatNumber",
    ] as const;
    for (const field of textFields) {
      payload[field] = payload[field] || null;
    }
    return payload;
  }
}
